// BUILD-049: naming (and clearing) the person who DOES one checklist task, for onboarding AND offboarding checklists, from the HR employee checklist panel.
//
// Company-scope HR only: the panel passes `assigneeAdmin` solely from HR Administration, and the server re-checks company-scope hr.checklist.assign on every call
// (a person without it gets a refusal, shown here as the server's own sentence). Any active employee with Unique HQ access may be named - HR access is not needed and
// naming grants none: the person sees only this task in My Work and may start it, add a task note and complete ordinary work. The person list is the company directory;
// the server decides who is eligible right now (active, HQ access) and says so if the choice is refused.
import { useCallback, useEffect, useState } from 'react';
import {
  getCompanyDirectory, listHrChecklistAssignees, setHrChecklistTaskAssignees,
  type CompanyDirectoryPerson, type HrChecklistAssigneeOverlay,
} from '../../../api/client';

export type OverlayStatus = 'idle' | 'loading' | 'ready' | 'error';

/** The named assignees of one employee's checklists, by task id. `reload` re-reads after a change. */
export function useAssigneeOverlay(subjectEmployeeId: string | undefined, reloadKey: unknown) {
  const [byTask, setByTask] = useState<Record<string, HrChecklistAssigneeOverlay>>({});
  const [status, setStatus] = useState<OverlayStatus>('idle');
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!subjectEmployeeId) { setStatus('idle'); return; }
    let cancelled = false;
    setStatus('loading');
    listHrChecklistAssignees(subjectEmployeeId)
      .then((r) => { if (!cancelled) { setByTask(Object.fromEntries(r.assignees.map((a) => [a.assignmentTaskId, a]))); setStatus('ready'); } })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [subjectEmployeeId, reloadKey, tick]);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { byTask, status, reload };
}

export function TaskAssigneeControl({
  assignmentId, assignmentTaskId, taskText, taskStatus, overlay, overlayStatus, onChanged,
}: {
  assignmentId: string;
  assignmentTaskId: string;
  taskText: string;
  taskStatus: string;
  overlay: HrChecklistAssigneeOverlay | undefined;
  overlayStatus: OverlayStatus;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState<CompanyDirectoryPerson[] | null>(null);
  const [peopleError, setPeopleError] = useState(false);
  const [choice, setChoice] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const closed = taskStatus === 'completed' || taskStatus === 'exception';

  async function openPicker() {
    setOpen(true); setError(''); setChoice(overlay?.assigneeEmployeeId ?? '');
    if (people === null && !peopleError) {
      try { setPeople((await getCompanyDirectory()).people); } catch { setPeopleError(true); }
    }
  }
  async function save(assigneeEmployeeId: string | null) {
    setSaving(true); setError('');
    try {
      await setHrChecklistTaskAssignees(assignmentId, [{ assignmentTaskId, assigneeEmployeeId }]);
      setOpen(false);
      onChanged();
    } catch (e: any) {
      setError(e?.message || 'The assignee was not saved. Nothing was changed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-1 ml-4 text-xs" data-testid="checklist-task-assignee">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-gray-500">Assigned to:</span>
        {overlayStatus === 'error' ? (
          <span className="text-amber-800" data-testid="checklist-assignee-unavailable">Assignee information could not be loaded.</span>
        ) : overlay ? (
          <>
            <span className="font-medium text-gray-800" data-testid="checklist-assignee-name">{overlay.assigneeName ?? 'An employee'}</span>
            {overlay.assigneeActive === false && <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-700" data-testid="checklist-assignee-inactive">No longer active — choose someone else</span>}
          </>
        ) : (
          <span className="text-gray-500" data-testid="checklist-assignee-none">Nobody named</span>
        )}
        {!closed && !open && overlayStatus !== 'error' && (
          <button type="button" onClick={() => void openPicker()} className="text-[#176A9B] hover:underline" data-testid="checklist-assignee-change">
            {overlay ? 'Change' : 'Name a person'}<span className="sr-only"> for {taskText}</span>
          </button>
        )}
      </div>
      {open && (
        <div className="mt-1 flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-2" data-testid="checklist-assignee-picker">
          <select className="max-w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs" value={choice} onChange={(e) => setChoice(e.target.value)} aria-label={`Person to do ${taskText}`} data-testid="checklist-assignee-select">
            <option value="">{people === null ? (peopleError ? 'The people list could not be loaded' : 'Loading people…') : 'Choose a person…'}</option>
            {(people ?? []).map((p) => <option key={p.employeeId} value={p.employeeId}>{p.name}{p.department ? ` — ${p.department}` : ''}</option>)}
          </select>
          <button type="button" disabled={saving || !choice || choice === overlay?.assigneeEmployeeId} onClick={() => void save(choice)} className="rounded bg-[#4AABDE] px-3 py-1 text-xs text-white disabled:opacity-50" data-testid="checklist-assignee-save">{saving ? 'Saving…' : 'Save'}</button>
          {overlay && <button type="button" disabled={saving} onClick={() => void save(null)} className="text-xs text-[#176A9B] hover:underline" data-testid="checklist-assignee-clear">Clear</button>}
          <button type="button" disabled={saving} onClick={() => setOpen(false)} className="text-xs text-gray-600 hover:underline">Cancel</button>
          <span className="w-full text-[11px] text-gray-500">The person sees this one task in My Work and can start it, add a task note and mark ordinary work done. Naming someone gives no access to HR records. A task tied to a document is completed through the HR document review.</span>
          {error && <span role="alert" className="w-full rounded border border-red-200 bg-red-50 px-2 py-1 text-red-700" data-testid="checklist-assignee-error">{error}</span>}
        </div>
      )}
    </div>
  );
}
